import { useCallback, useEffect, useState } from 'react';
import { coupons as couponsApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { Loading, Empty, Modal, Can, Pagination, money, dateOnly } from '../components/Common';

export default function Coupons() {
  const toast = useToast();

  const [data, setData] = useState({ coupons: [], meta: null });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await couponsApi.list({ page, limit: 20 });
      setData(res.data);
    } catch (err) {
      toast.error('Could not load coupons', err.message);
    } finally {
      setLoading(false);
    }
  }, [page, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'Coupons · UrbanClean'; }, []);

  const toggle = async (coupon) => {
    try {
      const res = await couponsApi.toggle(coupon._id);
      toast.success(res.message);
      load();
    } catch (err) {
      toast.error('Could not update', err.message);
    }
  };

  const remove = async (coupon) => {
    if (!window.confirm(`Delete the coupon ${coupon.code}?`)) return;
    try {
      await couponsApi.remove(coupon._id);
      toast.success('Coupon deleted');
      load();
    } catch (err) {
      toast.error('Could not delete', err.message);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Coupons</h1>
          <div className="sub">Offers shown in the app and validated at checkout</div>
        </div>
        <Can permission="coupon.create">
          <button className="btn btn-blue" onClick={() => setEditing({})}>+ New coupon</button>
        </Can>
      </div>

      <div className="card">
        {loading ? <Loading /> : data.coupons.length === 0 ? (
          <Empty title="No coupons yet" body="Create one to run your first offer." />
        ) : (
          <>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr><th>Code</th><th>Discount</th><th>Conditions</th><th className="center">Used</th><th>Valid till</th><th>Status</th><th></th></tr>
                </thead>
                <tbody>
                  {data.coupons.map((c) => (
                    <tr key={c._id}>
                      <td>
                        <div className="strong mono">{c.code}</div>
                        <div className="muted" style={{ fontSize: 12 }}>{c.title}</div>
                      </td>
                      <td className="strong">
                        {c.discountType === 'flat' ? money(c.discountValue) : `${c.discountValue}%`}
                        {c.maxDiscount ? <div className="muted" style={{ fontSize: 11.5, fontWeight: 400 }}>max {money(c.maxDiscount)}</div> : null}
                      </td>
                      <td className="muted" style={{ fontSize: 12.5 }}>
                        Min order {money(c.minOrderValue)}
                        {c.firstOrderOnly ? <div><span className="badge badge-blue">First order only</span></div> : null}
                      </td>
                      <td className="center">{c.usedCount}{c.usageLimit ? ` / ${c.usageLimit}` : ''}</td>
                      <td className="muted nowrap">{dateOnly(c.validTill)}</td>
                      <td>
                        <span className={`badge badge-${c.isActive && new Date(c.validTill) > new Date() ? 'green' : 'gray'}`}>
                          {new Date(c.validTill) < new Date() ? 'Expired' : c.isActive ? 'Active' : 'Paused'}
                        </span>
                      </td>
                      <td className="right nowrap">
                        <Can permission="coupon.update">
                          <button className="btn btn-sm btn-ghost" onClick={() => setEditing(c)}>Edit</button>
                          <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6 }} onClick={() => toggle(c)}>
                            {c.isActive ? 'Pause' : 'Activate'}
                          </button>
                        </Can>
                        <Can permission="coupon.delete">
                          <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6, color: 'var(--red)' }} onClick={() => remove(c)}>Delete</button>
                        </Can>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination meta={data.meta} onPage={setPage} />
          </>
        )}
      </div>

      <CouponEditor coupon={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </>
  );
}

function CouponEditor({ coupon, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!coupon) return;
    setForm({
      code: coupon.code || '', title: coupon.title || '', description: coupon.description || '',
      discountType: coupon.discountType || 'percent',
      discountValue: coupon.discountValue ?? '',
      maxDiscount: coupon.maxDiscount ?? '',
      minOrderValue: coupon.minOrderValue ?? 0,
      usageLimit: coupon.usageLimit ?? 0,
      usageLimitPerUser: coupon.usageLimitPerUser ?? 1,
      firstOrderOnly: Boolean(coupon.firstOrderOnly),
      validTill: coupon.validTill ? String(coupon.validTill).slice(0, 10) : '',
    });
  }, [coupon]);

  if (!coupon) return null;
  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!form.code || form.discountValue === '' || !form.validTill) {
      return toast.error('Code, discount and expiry date are required');
    }
    setBusy(true);
    try {
      const body = {
        ...form,
        code: form.code.toUpperCase(),
        discountValue: Number(form.discountValue),
        maxDiscount: form.maxDiscount === '' ? null : Number(form.maxDiscount),
        minOrderValue: Number(form.minOrderValue),
        usageLimit: Number(form.usageLimit),
        usageLimitPerUser: Number(form.usageLimitPerUser),
      };
      if (coupon._id) await couponsApi.update(coupon._id, body);
      else await couponsApi.create(body);
      toast.success('Coupon saved');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={coupon._id ? `Edit — ${coupon.code}` : 'New coupon'} onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save coupon'}</button>
      </>}>
      <div className="row">
        <div className="field">
          <label>Code *</label>
          <input className="input mono" value={form.code || ''} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="FIRST50" />
        </div>
        <div className="field"><label>Title</label><input className="input" value={form.title || ''} onChange={set('title')} placeholder="Flat 50% off" /></div>
      </div>
      <div className="row">
        <div className="field">
          <label>Discount type</label>
          <select className="select" value={form.discountType} onChange={set('discountType')}>
            <option value="percent">Percentage</option>
            <option value="flat">Flat amount</option>
          </select>
        </div>
        <div className="field"><label>Value *</label><input className="input" type="number" value={form.discountValue} onChange={set('discountValue')} /></div>
        <div className="field"><label>Max discount</label><input className="input" type="number" value={form.maxDiscount} onChange={set('maxDiscount')} placeholder="No cap" /></div>
      </div>
      <div className="row">
        <div className="field"><label>Min order value</label><input className="input" type="number" value={form.minOrderValue} onChange={set('minOrderValue')} /></div>
        <div className="field"><label>Valid till *</label><input className="input" type="date" value={form.validTill} onChange={set('validTill')} /></div>
      </div>
      <div className="row">
        <div className="field">
          <label>Total usage limit</label>
          <input className="input" type="number" value={form.usageLimit} onChange={set('usageLimit')} />
          <div className="hint">0 = unlimited</div>
        </div>
        <div className="field"><label>Per customer limit</label><input className="input" type="number" value={form.usageLimitPerUser} onChange={set('usageLimitPerUser')} /></div>
      </div>
      <div className="field">
        <label className="switch">
          <input type="checkbox" checked={form.firstOrderOnly} onChange={set('firstOrderOnly')} />
          <span className="track" /><span>First order only</span>
        </label>
      </div>
    </Modal>
  );
}
